import { Group, Paper, Stack, Text } from '@mantine/core';
import type {
  NotificationField,
  NotificationRole,
  NotificationSettingsChanges,
  NotificationSettingsScope,
  NotificationTypeView,
} from '../../types/notification-settings';
import { RoleCell } from './role-cell';
import { TextOverrideField } from './text-override-field';
import { hasChange, pendingValue, ROLE_LABELS } from './settings-changes';

interface NotificationTypeCardProps {
  type: string;
  roles: NotificationRole[];
  view: NotificationTypeView;
  scope: NotificationSettingsScope;
  changes: NotificationSettingsChanges;
  onChange: (field: NotificationField, value: boolean | string | null) => void;
}

export function NotificationTypeCard({
  type,
  roles,
  view,
  scope,
  changes,
  onChange,
}: NotificationTypeCardProps) {
  const inheritLabel = scope === 'global' ? 'Default' : 'Inherit';
  return (
    <Paper withBorder p="md">
      <Stack spacing="sm">
        <Group spacing="xs" align="baseline">
          <Text weight={600}>{view.description ?? type}</Text>
          <Text size="xs" color="dimmed" sx={{ fontFamily: 'monospace' }}>
            {type}
          </Text>
        </Group>
        <Group spacing="xl" align="flex-start">
          {roles.map((role) => {
            const state = view.roles[role];
            return state ? (
              <RoleCell
                key={role}
                label={ROLE_LABELS[role]}
                state={state}
                value={pendingValue(changes, type, role, state.stored)}
                inheritLabel={inheritLabel}
                changed={hasChange(changes, type, role)}
                onChange={(value) => onChange(role, value)}
              />
            ) : null;
          })}
        </Group>
        {scope === 'global' && (
          <TextOverrideField
            label="Label shown on the customer settings page"
            value={pendingValue(changes, type, 'description', view.description)}
            inheritedText={type}
            multiline={false}
            changed={hasChange(changes, type, 'description')}
            onChange={(value) => onChange('description', value)}
          />
        )}
        <TextOverrideField
          label="Template"
          value={pendingValue(changes, type, 'template', view.template.stored)}
          inheritedText={view.template.inherited ?? 'Built-in default template'}
          multiline
          changed={hasChange(changes, type, 'template')}
          onChange={(value) => onChange('template', value)}
        />
      </Stack>
    </Paper>
  );
}
